import { useState } from 'react'
import { User, X } from 'lucide-react'
import { clientApi } from '../../api/client-portal.js'
import type { GuestIdentity } from '../../utils/clientGuestIdentity.js'

interface GuestIdentityFormProps {
  tenantSlug: string
  onSubmit: (identity: GuestIdentity) => void
  onClose: () => void
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

/**
 * Email-first identity gate for Call Support.
 *
 * Step 1 asks only for email, then checks `/call/client-lookup` — a
 * returning caller whose email already has a name on file skips straight to
 * the call. A new (or nameless) email advances to step 2, which asks for a
 * name too; the actual `clients` row is created once by the /call/session
 * mint call this identity feeds into (see call-session.ts's ClientResolver
 * wiring), not here — this form only decides which fields to ask for.
 */
export function GuestIdentityForm({ tenantSlug, onSubmit, onClose }: GuestIdentityFormProps) {
  const [step, setStep] = useState<'email' | 'name'>('email')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [touched, setTouched] = useState(false)
  const [checking, setChecking] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)

  const emailError = touched && !isValidEmail(email.trim()) ? 'Please enter a valid email address.' : null
  const nameError = touched && !name.trim() ? 'Please enter your name.' : null

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched(true)
    setLookupError(null)
    const trimmedEmail = email.trim()
    if (!isValidEmail(trimmedEmail)) return

    setChecking(true)
    try {
      const res = await clientApi.post<{ exists: boolean; name: string | null }>(
        `/portal/${tenantSlug}/call/client-lookup`,
        { email: trimmedEmail },
      )
      if (res.exists && res.name) {
        onSubmit({ name: res.name, email: trimmedEmail })
        return
      }
      setTouched(false)
      setStep('name')
    } catch {
      // Lookup failing shouldn't strand the visitor — fall back to asking
      // for a name, same as a brand-new caller. /call/session's own
      // ClientResolver call still resolves/creates the client either way.
      setLookupError('Could not verify your email — please continue.')
      setTouched(false)
      setStep('name')
    } finally {
      setChecking(false)
    }
  }

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (!name.trim()) return
    onSubmit({ name: name.trim(), email: email.trim() })
  }

  return (
    <div className="kc3-widget-panel">
      <button type="button" className="kc3-widget-close" onClick={onClose} aria-label="Close">
        <X className="h-3 w-3" />
      </button>
      <div className="kc3-widget h-full overflow-hidden flex flex-col bg-white">
        <div className="flex items-center gap-3 px-5 py-5 bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex-shrink-0">
          <div className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <User className="h-6 w-6" />
          </div>
          <div>
            <p className="font-semibold text-base leading-tight">Before we start</p>
            <p className="text-sm text-white/80">
              {step === 'email' ? "What's your email?" : 'Tell us a bit about yourself'}
            </p>
          </div>
        </div>

        {step === 'email' && (
          <form onSubmit={handleEmailSubmit} className="flex-1 flex flex-col p-5 gap-4">
            <div>
              <label htmlFor="guest-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                id="guest-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
              />
              {emailError && <p className="mt-1 text-xs text-red-600">{emailError}</p>}
            </div>

            <button
              type="submit"
              disabled={checking}
              className="mt-auto w-full px-5 py-3 bg-blue-600 text-white rounded-2xl text-sm font-medium hover:bg-blue-700 transition disabled:opacity-60"
            >
              {checking ? 'Checking…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'name' && (
          <form onSubmit={handleNameSubmit} className="flex-1 flex flex-col p-5 gap-4">
            {lookupError && <p className="text-xs text-amber-600">{lookupError}</p>}
            <div>
              <label htmlFor="guest-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                Name
              </label>
              <input
                id="guest-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoFocus
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
              />
              {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
            </div>

            <p className="text-[11px] text-gray-500 leading-normal">
              By continuing, you acknowledge our{' '}
              <a
                href="/client-portal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Privacy Notice
              </a>
              .
            </p>

            <button
              type="submit"
              className="mt-auto w-full px-5 py-3 bg-blue-600 text-white rounded-2xl text-sm font-medium hover:bg-blue-700 transition"
            >
              Continue
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
